// src/new/components/monitor/TriageChat/vedaProtocol.ts

export interface UserMessageEnvelope {
  type: 'MESSAGE_CREATE';
  request_id: string;
  payload: {
    id: string;
    session_id: string;
    type: 'user_message';
    role: 'user';
    status: 'completed';
    // Request veda-ai's read-only Triage mode for every message in this chat:
    // it overlays an investigate-first, confirm-before-mutate prompt and attaches
    // the read-only Monitoring MCP toolset. (UserRequestedTools.TRIAGE.)
    tools: ['triage'];
    content: Array<{
      type: 'user_text_prompt';
      text: string;
      active_agent: null;
      persistent: true;
      include_in_context: true;
      metadata: null;
      annotations: [];
    }>;
    conversation_message_order: number;
    conversation_branch_message_order: number;
  };
}

/** Build the exact MESSAGE_CREATE envelope veda-ai expects for a user message. */
export function buildUserMessageEnvelope(
  sessionId: string,
  text: string,
  requestId: string,
  uuid: () => string,
  order: number,
): UserMessageEnvelope {
  return {
    type: 'MESSAGE_CREATE',
    request_id: requestId,
    payload: {
      id: uuid(),
      session_id: sessionId,
      type: 'user_message',
      role: 'user',
      status: 'completed',
      tools: ['triage'],
      content: [
        {
          type: 'user_text_prompt',
          text,
          active_agent: null,
          persistent: true,
          include_in_context: true,
          metadata: null,
          annotations: [],
        },
      ],
      conversation_message_order: order,
      conversation_branch_message_order: order,
    },
  };
}

export interface StopEnvelope {
  type: 'STOP_GENERATION';
  request_id: string;
  payload: { reason: string };
}

export function buildStopEnvelope(requestId: string, reason = 'User stopped'): StopEnvelope {
  return {
    type: 'STOP_GENERATION',
    request_id: requestId,
    payload: { reason },
  };
}

export type ServerEvent =
  | { kind: 'ack' }
  | { kind: 'pong' }
  | { kind: 'text'; messageId: string; text: string; done: boolean }
  | { kind: 'tool'; label: string; done: boolean }
  | { kind: 'error'; message: string }
  | { kind: 'ignore' };

/** Parse a raw server frame into a small, UI-friendly event. */
export function parseServerEnvelope(raw: string): ServerEvent {
  let env: any;
  try {
    env = JSON.parse(raw);
  } catch {
    return { kind: 'ignore' };
  }
  if (!env || typeof env !== 'object') return { kind: 'ignore' };

  switch (env.type) {
    case 'ACK':
      return { kind: 'ack' };
    case 'PONG':
      return { kind: 'pong' };
    case 'ERROR':
      return { kind: 'error', message: env.payload?.message ?? 'Unknown error' };
    case 'MESSAGE_CREATE': {
      const payload = env.payload ?? {};
      const block = Array.isArray(payload.content) ? payload.content[0] : undefined;
      const done = payload.status === 'completed';
      if (!block) return { kind: 'ignore' };
      if (block.type === 'agent_text_response' || block.type === 'text') {
        return { kind: 'text', messageId: payload.id, text: block.text ?? '', done };
      }
      if (block.type === 'tool_request') {
        return {
          kind: 'tool',
          label: block.tool_display_name || block.tool_name || 'Tool call',
          done,
        };
      }
      return { kind: 'ignore' };
    }
    default:
      return { kind: 'ignore' };
  }
}
